"use client";

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  analyticsInit,
  analyticsHeartbeat,
  analyticsPageView,
  analyticsExit,
  analyticsSyncCart,
} from "../../utils/analytics";
import { KISSARITI_CART_STORAGE_EVENT } from "../../context/CartContext";

/**
 * Invisible tracker: sessions, pageviews, live heartbeats, cart snapshots, exits.
 */
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  const initialized = useRef(false);
  const cartDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    analyticsInit();
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    analyticsPageView(path);
    analyticsSyncCart(path);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onCartChange = () => {
      if (cartDebounce.current) clearTimeout(cartDebounce.current);
      cartDebounce.current = setTimeout(() => {
        analyticsSyncCart(location.pathname + location.search);
      }, 800);
    };
    window.addEventListener(KISSARITI_CART_STORAGE_EVENT, onCartChange);
    return () => {
      window.removeEventListener(KISSARITI_CART_STORAGE_EVENT, onCartChange);
      if (cartDebounce.current) clearTimeout(cartDebounce.current);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        analyticsHeartbeat();
      }
    }, 60000);

    const onHide = () => {
      if (document.visibilityState === "hidden") {
        analyticsExit();
      }
    };

    const onUnload = () => {
      analyticsExit();
    };

    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onUnload);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onUnload);
    };
  }, []);

  return null;
};

export default AnalyticsTracker;
